import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { PropsWithChildren, ReactNode } from 'react';

import { Icon } from '../icon';
import { toRem } from '../../utils/ui';
import { conditionalSpread } from '../../utils/helper-utils';

import './collapse.scss';

const HEIGHT_EASE = [0.25, 1, 0.5, 1] as const;

export const Collapse = (props: CollapseProps) => {
  const {
    top,
    open,
    label,
    bottom,
    onChange,
    children,
    className,
    iconSize = 16,
    holderClassName,
    labelHolderClassName,
    labelTestId,
    unmountOnCollapse = false,
  } = props;

  const reduceMotion = useReducedMotion();

  const content = (
    <div
      className="collapse-children-holder"
      style={{
        ...conditionalSpread({ marginTop: toRem(top) }, !!top),
        ...conditionalSpread({ marginBottom: toRem(bottom) }, !!bottom),
      }}
    >
      {children}
    </div>
  );

  const motionClassName = clsx('collapse-css-transition', className);

  // Open uses height: 'auto' so layout remasures (dock slot, --max-height) do not retarget Motion.
  const openTransition = reduceMotion
    ? {
        height: { duration: 0 },
        opacity: { duration: 0.15 },
      }
    : {
        height: { duration: 0.25, ease: HEIGHT_EASE },
        opacity: { duration: 0.2, ease: 'easeOut' as const },
      };

  const exitTransition = reduceMotion
    ? {
        height: { duration: 0 },
        opacity: { duration: 0.12 },
      }
    : {
        height: { duration: 0.2, ease: HEIGHT_EASE },
        opacity: { duration: 0.01 },
      };

  const renderContent = () => {
    if (!unmountOnCollapse) {
      return (
        <motion.div
          className={motionClassName}
          initial={false}
          animate={{
            height: open ? 'auto' : 0,
            opacity: open ? 1 : 0,
          }}
          transition={openTransition}
          style={{ overflow: 'hidden' }}
        >
          {content}
        </motion.div>
      );
    }

    return (
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="collapse-content"
            className={motionClassName}
            initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={
              reduceMotion ? { opacity: 0, transition: exitTransition } : { height: 0, opacity: 1, transition: exitTransition }
            }
            transition={openTransition}
            style={{ overflow: 'hidden' }}
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return label ? (
    <div className={clsx('collapse-holder', holderClassName)}>
      <ul className={clsx('collapse-label-holder', labelHolderClassName)} onClick={() => onChange?.(!open)}>
        <li className={clsx({ open })} data-testid={labelTestId}>
          <Icon type="arrow-down-01" size={iconSize} />
        </li>
        <li>{label}</li>
      </ul>
      {renderContent()}
    </div>
  ) : (
    renderContent()
  );
};

interface CollapseProps extends PropsWithChildren {
  top?: number;
  open: boolean;
  bottom?: number;
  iconSize?: number;
  label?: ReactNode;
  className?: string;
  holderClassName?: string;
  unmountOnCollapse?: boolean;
  labelHolderClassName?: string;
  labelTestId?: string;
  onChange?: (open: boolean) => void;
}
